<template>
  <section
    v-if="loading || campaigns.length"
    class="carousel-ad"
    :class="`carousel-ad--${variant}`"
    :style="slotStyle"
    :aria-label="`Publicidade: ${placementKey}`"
    tabindex="0"
    @mouseenter="paused = true"
    @mouseleave="paused = false"
    @keydown.left.prevent="previous"
    @keydown.right.prevent="next"
  >
    <div v-if="loading" class="carousel-ad__skeleton" aria-hidden="true"></div>

    <template v-else>
      <article
        v-for="(campaign, index) in campaigns"
        :key="campaign.id"
        class="carousel-ad__slide"
        :class="{ 'is-active': index === activeIndex, 'is-clickable': isNavigable(campaign) }"
        :aria-hidden="index !== activeIndex"
        :role="campaign.link ? 'link' : undefined"
        :tabindex="campaign.link && index === activeIndex ? 0 : -1"
        @click="openCampaign(campaign)"
        @keydown.enter.prevent="openCampaign(campaign)"
      >
        <img
          :src="campaignImage(campaign)"
          :alt="campaign.alt_text || campaign.title || 'Publicidade Golden Key'"
          :loading="index === 0 ? 'eager' : 'lazy'"
          @error="hideBrokenCampaign(campaign)"
        />
        <span v-if="campaign.title" class="carousel-ad__title">{{ campaign.title }}</span>
      </article>

      <template v-if="campaigns.length > 1">
        <button class="carousel-ad__arrow carousel-ad__arrow--prev" type="button" aria-label="Publicidade anterior" @click.stop="previous">‹</button>
        <button class="carousel-ad__arrow carousel-ad__arrow--next" type="button" aria-label="Publicidade seguinte" @click.stop="next">›</button>
        <div class="carousel-ad__dots" aria-label="Selecionar publicidade">
          <button
            v-for="(_, index) in campaigns"
            :key="index"
            type="button"
            :class="{ 'is-active': index === activeIndex }"
            :aria-label="`Publicidade ${index + 1}`"
            :aria-current="index === activeIndex ? 'true' : undefined"
            @click.stop="activeIndex = index"
          ></button>
        </div>
      </template>
    </template>
  </section>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import apiClient from '@/config/api'

const props = defineProps({
  placementKey: { type: String, required: true },
  variant: { type: String, default: 'horizontal' },
})

const router = useRouter()
const fetchedItems = ref([])
const loading = ref(true)
const activeIndex = ref(0)
const paused = ref(false)
const isMobile = ref(window.innerWidth < 768)
const brokenCampaignIds = ref([])
let timer = null

const campaigns = computed(() => fetchedItems.value
  .filter((item) =>
    item &&
    item.active !== false &&
    item.image_url &&
    !brokenCampaignIds.value.includes(item.id),
  )
  .sort((a, b) => (a.order || 0) - (b.order || 0)))

const slotStyle = computed(() => {
  const placement = campaigns.value[activeIndex.value]?.placement
  const width = isMobile.value ? placement?.mobile_width : placement?.width
  const height = isMobile.value ? placement?.mobile_height : placement?.height
  return width && height ? { aspectRatio: `${width} / ${height}` } : undefined
})

async function fetchCampaigns() {
  loading.value = true
  fetchedItems.value = []
  brokenCampaignIds.value = []
  try {
    const { data } = await apiClient.get('/carousel', {
      params: { placement_key: props.placementKey },
    })
    const items = data?.data
    if (!Array.isArray(items)) {
      throw new Error('A resposta de publicidades do servidor é inválida.')
    }
    fetchedItems.value = items
  } catch (error) {
    console.error(`Não foi possível carregar o slot publicitário "${props.placementKey}":`, error)
  } finally {
    loading.value = false
  }
}

function next() {
  if (campaigns.value.length > 1) {
    activeIndex.value = (activeIndex.value + 1) % campaigns.value.length
  }
}

function previous() {
  if (campaigns.value.length > 1) {
    activeIndex.value = (activeIndex.value - 1 + campaigns.value.length) % campaigns.value.length
  }
}

function isNavigable(campaign) {
  return Boolean(campaign.link)
}

function openCampaign(campaign) {
  if (!campaign.link) return
  if (/^https?:\/\//i.test(campaign.link)) {
    window.open(campaign.link, '_blank', 'noopener,noreferrer')
  } else if (campaign.link.startsWith('/')) {
    router.push(campaign.link)
  }
}

function campaignImage(campaign) {
  return isMobile.value && campaign.mobile_image_url
    ? campaign.mobile_image_url
    : campaign.image_url
}

function hideBrokenCampaign(campaign) {
  if (!brokenCampaignIds.value.includes(campaign.id)) {
    brokenCampaignIds.value.push(campaign.id)
  }
}

function updateViewport() {
  isMobile.value = window.innerWidth < 768
}

watch(campaigns, (items) => {
  if (activeIndex.value >= items.length) activeIndex.value = 0
})

watch(() => props.placementKey, fetchCampaigns)

onMounted(() => {
  fetchCampaigns()
  window.addEventListener('resize', updateViewport)
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    timer = window.setInterval(() => {
      if (!paused.value) next()
    }, 7500)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateViewport)
  if (timer) window.clearInterval(timer)
})
</script>

<style scoped>
.carousel-ad { position: relative; width: 100%; overflow: hidden; background: #c9c2b6; }
.carousel-ad--horizontal { min-height: 80px; }
.carousel-ad--sidebar { min-height: 180px; }
.carousel-ad__skeleton { position: absolute; inset: 0; background: linear-gradient(90deg, #c9c2b6, #e6e0d6, #c9c2b6); background-size: 200% 100%; animation: ad-shimmer 1.6s linear infinite; }
.carousel-ad__slide { position: absolute; inset: 0; opacity: 0; pointer-events: none; transition: opacity .5s ease; }
.carousel-ad__slide.is-active { opacity: 1; pointer-events: auto; }
.carousel-ad__slide.is-clickable { cursor: pointer; }
.carousel-ad__slide:focus-visible { outline: 3px solid #eba301; outline-offset: -3px; }
.carousel-ad__slide img { width: 100%; height: 100%; display: block; object-fit: contain; }
.carousel-ad__title { position: absolute; left: 24px; bottom: 22px; max-width: calc(100% - 48px); padding: 8px 12px; color: white; background: rgba(20,17,14,.78); font-weight: 650; }
.carousel-ad__arrow { position: absolute; z-index: 3; top: 50%; width: 40px; height: 40px; border: 1px solid rgba(255,255,255,.6); border-radius: 50%; color: white; background: rgba(20,17,14,.48); transform: translateY(-50%); cursor: pointer; }
.carousel-ad__arrow--prev { left: 14px; }
.carousel-ad__arrow--next { right: 14px; }
.carousel-ad__dots { position: absolute; z-index: 3; right: 16px; bottom: 13px; display: flex; gap: 6px; }
.carousel-ad__dots button { width: 7px; height: 7px; padding: 0; border: 0; border-radius: 10px; background: rgba(255,255,255,.55); cursor: pointer; }
.carousel-ad__dots button.is-active { width: 22px; background: #eba301; }
.carousel-ad:focus-visible { outline: 3px solid #eba301; outline-offset: 2px; }
@keyframes ad-shimmer { to { background-position: -200% 0; } }
@media (prefers-reduced-motion: reduce) { .carousel-ad__slide { transition: none; } .carousel-ad__skeleton { animation: none; } }
</style>
